import { getApiUrl, getApiToken, isServer } from "./env";
import { getTrackingData } from "./tracking";

const LEGACY_STORAGE_PREFIXES = [
  "https://storagedtrading.blob.core.windows.net/media",
  "https://blobdc5websiteprodassets.blob.core.windows.net/media",
];
const NEW_STORAGE_PREFIX =
  "https://blobdc5websiteprodassets.blob.core.windows.net/container-dc5-website-prod-media";

// Get the url of the Strapi API
// On server: direct API call with token
// On client: use proxy endpoint (token stays on server)
export function getStrapiURL(path: string) {
  const apiUrl = getApiUrl();

  // On server: direct API call
  if (isServer()) {
    return `${apiUrl}${path}`;
  }

  // On client: use proxy endpoint (hides API URL and token)
  return `/api/proxy${path}`;
}

// Get API token (only available on server!)
function getApiTokenForRequest(): string {
  if (isServer()) {
    return getApiToken();
  }
  // On client, we don't have token - use proxy instead
  return "";
}

// get data from api
export function getStrapiResponse(path: string, method = "get") {
  const url = getStrapiURL(path);

  // On server: add token
  if (isServer()) {
    return fetch(url, {
      method: method,
      headers: new Headers({
        Authorization: "Bearer " + getApiTokenForRequest(),
      }),
    });
  }

  // On client: no token needed (proxy handles it)
  return fetch(url, {
    method: method,
    headers: {
      "Content-Type": "application/json",
    },
  });
}

// send data to api
export function sendStrapiRequest(path: string, data: object, method = "post") {
  const url = getStrapiURL(path);
  const enrichedData =
    path === "/api/forms" ? { ...data, ...getTrackingData() } : data;

  // On server: add token
  if (isServer()) {
    return fetch(url, {
      method: method,
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + getApiTokenForRequest(),
      },
      body: JSON.stringify({ data: enrichedData }),
    });
  }

  // On client: no token needed (proxy handles it)
  return fetch(url, {
    method: method,
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ data: enrichedData }),
  });
}

// Media URLs must be identical on SSR and the first client render or React hydration
// fails (#418). Always use the same-origin proxy; the Node server implements /api/media.
export function getStrapiMedia(url: string | undefined): string | undefined {
  if (!url) {
    return undefined;
  }
  if (url.startsWith("http") || url.startsWith("//")) {
    return normalizeLegacyStorageUrl(url);
  }

  return `/api/media${url}`;
}

export function normalizeLegacyStorageUrl(url: string): string {
  if (!url) return url;
  for (const fromPrefix of LEGACY_STORAGE_PREFIXES) {
    if (url.startsWith(fromPrefix)) {
      return `${NEW_STORAGE_PREFIX}${url.slice(fromPrefix.length)}`;
    }
  }
  return url;
}

// Build full URL for public Azure Blob assets (no secret token needed).
// Used to replace legacy hardcoded URLs in a few pages/components.
export function getBlobAssetUrl(
  fileOrPath: string | undefined,
): string | undefined {
  if (!fileOrPath) return undefined;

  // If it's already a full URL, keep it.
  if (fileOrPath.startsWith("http") || fileOrPath.startsWith("//")) {
    return fileOrPath;
  }

  // Extract file name (we expect inputs like "soc_1_xxx.svg" or paths).
  const fileName = fileOrPath.split("/").filter(Boolean).pop();
  if (!fileName) return fileOrPath;

  if (isServer()) {
    const storageAccount = process.env.STORAGE_ACCOUNT;
    const storageContainerName = process.env.STORAGE_CONTAINER_NAME;
    if (storageAccount && storageContainerName) {
      return `https://${storageAccount}.blob.core.windows.net/${storageContainerName}/assets/${fileName}`;
    }
    return fileOrPath;
  }

  const base = (window as Window & { __BLOB_ASSET_BASE__?: string })
    .__BLOB_ASSET_BASE__;
  if (base) return `${base}${fileName}`;

  // Must match SSR when STORAGE_* is unset (bare file id); avoid reading process.env in the browser bundle.
  return fileOrPath;
}

// fetch container
export async function fetchGlobalData(url: string) {
  const response = await getStrapiResponse(url);
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }
  return response.json();
}

// add locale to url
export function addLocaleData(url: string | undefined, locale: string) {
  if (locale === "uk") {
    locale = "ua";
  }
  if (locale) {
    if (url && url.indexOf("//") > -1) {
      return url;
    }

    if (url && url.indexOf("/" + locale) > -1) {
      return url;
    }

    url = "/" + locale + url;
  }

  return url;
}

// add locale to url
export function getCurrentLocation(url: string) {
  if (url && url.indexOf("/ua") === 0) {
    url = url.substring(3);
  }

  if (url && url.indexOf("/en") === 0) {
    url = url.substring(3);
  }

  if (url && url.indexOf("/pl") === 0) {
    url = url.substring(3);
  }

  if (url && url.indexOf("/ro") === 0) {
    url = url.substring(3);
  }

  if (url && url.indexOf("/hr") === 0) {
    url = url.substring(3);
  }

  if (!url) {
    url = "/";
  }

  return url;
}
